import api from '../api';

const INTERACTIVE_READ_TIMEOUT_MS = 15000;

export const createCanvas = (kind, name) =>
  api.post('/project/create-canvas', { kind, name }).then(r => r.data);

export const createUpload = (formData) =>
  api.post('/project/create-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);

export const listProjects = () =>
  api.get('/project/list').then(r => r.data.projects || []);

export const getProject = (id) =>
  api.get(`/project/${id}`, { timeout: INTERACTIVE_READ_TIMEOUT_MS }).then(r => r.data.project);

// attachUpload was removed here (contract: Prompt 4B.2A §19): every frontend
// file-storage path now goes through core/workspace/import/workspaceImportService
// (which owns validation and the completed-upload lifecycle guard) on top of
// utils/uploadClient's presigned transport. The legacy backend route
// POST /project/:projectId/attach-upload is deliberately LEFT IN PLACE - it is a
// separate backend surface to audit in a later phase, not part of this change.

export const sendChatMessage = (projectId, payload) =>
  api.post(`/chat/${projectId}`, payload).then(r => r.data);

export const getChatHistory = (projectId) =>
  api.get(`/chat/${projectId}`).then(r => r.data);

export const enableShare = (projectId) =>
  api.post(`/share/${projectId}`).then(r => r.data);

export const disableShare = (projectId) =>
  api.post(`/share/${projectId}`, { enable: false }).then(r => r.data);

export const getShareInfo = (projectId) =>
  api.get(`/share/${projectId}`).then(r => r.data);

export const getPublicProject = (token) =>
  api.get(`/share/t/${token}`).then(r => r.data);

export const getPresignedUrl = (projectId, key) =>
  api.get(`/project/${projectId}/sign`, { params: { key } }).then(r => r.data.url);

export const renameProject = (id, name) =>
  api.put(`/project/${id}`, { name }).then(r => r.data.project);

export const deleteProject = (id) =>
  api.delete(`/project/${id}`).then(r => r.data);

/**
 * Resolve the account's one persistent DoARSpace world. The server owns
 * migration, idempotency and project-quota semantics so multiple tabs/devices
 * can never race into creating duplicate worlds. Interactive boot must also
 * fail within a bounded period so the UI can offer Retry instead of spinning.
 */
export const getPrimaryWorld = () =>
  api.post('/doar/world', {}, { timeout: INTERACTIVE_READ_TIMEOUT_MS }).then(r => r.data.world);

export const getOrCreateDefaultGeoSpace = getPrimaryWorld;

export const generateMotion = (projectId, payload) =>
  api.post(`/projects/${projectId}/motion/generate`, payload).then(r => r.data);

export const publishReality = (payload) =>
  api.post('/reality/publish', payload).then(r => r.data);

export const getRealityBySlug = (slug) =>
  api.get(`/reality/${encodeURIComponent(slug)}`).then(r => r.data);

export const getRealityEmbed = (slug) =>
  api.get(`/reality/${encodeURIComponent(slug)}/embed`).then(r => r.data);

export const trackRealityEvent = (slug, eventType) => {
  if (!slug || !eventType) return Promise.resolve();
  return api
    .post(`/reality/${encodeURIComponent(slug)}/event`, { eventType })
    .then(r => r.data)
    .catch(() => {});
};

export const createRealityCollection = (payload) =>
  api.post('/reality-collections', payload).then(r => r.data);

export const getMyRealityCollections = (params = {}) => {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  return api.get(`/reality-collections/mine?${q}`).then(r => r.data);
};

export const getRealityCollectionBySlug = (slug) =>
  api.get(`/reality-collections/${encodeURIComponent(slug)}`).then(r => r.data);

export const updateRealityCollection = (id, updates) =>
  api.patch(`/reality-collections/${encodeURIComponent(id)}`, updates).then(r => r.data);

export const deleteRealityCollection = (id) =>
  api.delete(`/reality-collections/${encodeURIComponent(id)}`).then(r => r.data);

export const getRealityCollectionEmbed = (slug) =>
  api.get(`/reality-collections/${encodeURIComponent(slug)}/embed`).then(r => r.data);

export const createRealityCampaign = (payload) =>
  api.post('/reality-campaigns', payload).then(r => r.data);

export const getMyRealityCampaigns = (params = {}) => {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  return api.get(`/reality-campaigns/mine?${q}`).then(r => r.data);
};

export const getRealityCampaignBySlug = (slug) =>
  api.get(`/reality-campaigns/${encodeURIComponent(slug)}`).then(r => r.data);

export const updateRealityCampaign = (id, updates) =>
  api.patch(`/reality-campaigns/${encodeURIComponent(id)}`, updates).then(r => r.data);

export const deleteRealityCampaign = (id) =>
  api.delete(`/reality-campaigns/${encodeURIComponent(id)}`).then(r => r.data);

export const trackCampaignEvent = (slug, eventType) =>
  api.post(`/reality-campaigns/${encodeURIComponent(slug)}/event`, { eventType }).catch(() => {});

export const getRealityCampaignEmbed = (slug) =>
  api.get(`/reality-campaigns/${encodeURIComponent(slug)}/embed`).then(r => r.data);

export const updateReality = (id, updates) =>
  api.patch(`/reality/${encodeURIComponent(id)}`, updates).then(r => r.data);

export const deleteReality = (id) =>
  api.delete(`/reality/${encodeURIComponent(id)}`).then(r => r.data);

export const getMyRealities = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit)  q.set('limit',  String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  return api.get(`/reality/mine?${q}`).then(r => r.data);
};

export const getExploreRealities = (params = {}) => {
  const q = new URLSearchParams();
  if (params.type)      q.set('type',      params.type);
  if (params.dimension) q.set('dimension', params.dimension);
  if (params.limit)     q.set('limit',     String(params.limit));
  if (params.cursor)    q.set('cursor',    params.cursor);
  return api.get(`/reality/explore?${q}`).then(r => r.data);
};

export const buildThisReality = (idOrSlug) =>
  api.post(`/reality/${encodeURIComponent(idOrSlug)}/build-this`).then(r => r.data);

export { INTERACTIVE_READ_TIMEOUT_MS };
